/**
 * Toplu Üretim — the reference's `data-screen="import"`.
 *
 * Ported against the rendered DOM of `Screens.import`, `renderImport`, `renderValidation`,
 * `renderBatchHistory` and `renderBatchDetail` (`m07-import.js`). One screen serves two addresses,
 * exactly as upstream does: `/admin/import` is the five-step wizard, and `/admin/import?batch=<id>`
 * (or `?tab=history`) is the upload ledger and a single batch's row-by-row result.
 *
 * The flow's promise is in the lead paragraph and the code keeps it: uploading a file writes
 * nothing and sends nothing. A row becomes a credential only after column mapping, validation and
 * an explicit confirmation, and the recipient's page stays empty until they accept (rule 12).
 *
 * Flow state lives in a module-level `IMP`, as it does upstream — leaving the screen and coming
 * back keeps the step, the file and the decisions already made; only `resetImport` clears them.
 */
import { type ReactElement, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAdoptBatches } from '../data/adopt';
import { useV07Commands } from '../data/commands-context';
import type { BatchLookup } from '../data/queries';
import { ROWS_PER_PAGE } from '../data/queries-api';
import { useV07Queries } from '../data/queries-context';
import { useV07Role } from '../data/session';
import { useDemoData, useSettings } from '../data/store';
import type { DemoBatch, DemoCredential, DemoDatabase, DemoRelease, DemoTemplate } from '../data/types';
import { useRead } from '../data/use-read';
import {
  buildRenderData,
  type Decision,
  type DecisionContext,
  decisionFor,
  downloadText,
  generateUniquePublicCode,
  importFieldLabel,
  importFields,
  importRelease,
  mockRows,
  parseSpreadsheet,
  relValidityMonths,
  renderPreviewEmail,
  requiredFields,
  type Sheet,
  sampleCSV,
  sha256Canonical,
  shortCode,
  slugify,
  suggestField,
  uid,
  type ValidatedRow,
  validateRows,
  XLSX_LIMITS,
  xlsxSupported,
} from '../lib/import-flow';
import {
  batchRowOutcome,
  batchSummary,
  credentialViewState,
  currentRelease,
  deliveryLabel,
  fmtDate,
  fmtDateTime,
  fmtNum,
  publishedTemplates,
  STATUS_ICON,
  STATUS_PILL,
  STATUS_TR,
  stateForPolicy,
  TEMPLATE_STATUS_TR,
  trLower,
  trUpper,
  type ViewState,
} from '../lib/reference';
import {
  buildTerms,
  canvasH,
  canvasW,
  credentialSVG,
  holderFieldKey,
  type Layout,
  type RenderData,
  resolveRenderPlan,
} from '../lib/render';
import { runAcceptedNotice } from '../lib/batch-run-notice';
import { draftTitle, prototypeNowUTC, templateTypeLabel } from '../lib/template-list';
import { uploadFilename, uploadLabel, uploadName, uploadSubLabel } from '../lib/upload-name';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';
import { FirstSendPanel } from './FirstSendPanel';

const ACTOR = 'Türker Baş';

interface ImportFlowState {
  step: number;
  tplId: string | null;
  file: string | null;
  /**
   * The bytes the user chose, kept for the product's own import command: the backend re-reads and
   * re-validates the file server-side (rule 11, fail-closed), so the browser's parse below is a
   * preview of what will happen, never the thing that decides what is issued.
   */
  fileBytes: ArrayBuffer | null;
  sheets: Sheet[] | null;
  sheetIndex: number;
  headers: string[];
  rows: string[][];
  mapping: Record<string, string>;
  fixes: Record<number, Record<string, string>>;
  skip: number[];
  dupePolicy: string;
  rowChoice: Record<number, string>;
  overflowChoice: Record<number, string>;
  filter: string;
  lang: 'tr' | 'en';
  sendEmail: boolean;
  attemptKey: string | null;
  /**
   * «Stop READING the run» for the run in flight — not a React lifetime.
   *
   * It lives on this module-level wizard state on purpose: the step subtree is re-keyed on every
   * render and the app runs under `StrictMode`, so anything held in a `useRef` with an unmount
   * effect is torn down WHILE a run is in flight. A loop watching the server was being told to
   * stop by a re-render. Nothing but the operator closing the progress window flips this, and
   * flipping it stops the reading and never the run.
   */
  watch: { aborted: boolean } | null;
  batchId: string | null;
  issuing: boolean;
}

const IMP: ImportFlowState = {
  step: 1,
  tplId: null,
  file: null,
  fileBytes: null,
  sheets: null,
  sheetIndex: 0,
  headers: [],
  rows: [],
  mapping: {},
  fixes: {},
  skip: [],
  dupePolicy: 'skip',
  rowChoice: {},
  overflowChoice: {},
  filter: 'all',
  lang: 'tr',
  sendEmail: true,
  attemptKey: null,
  watch: null,
  batchId: null,
  issuing: false,
};

function resetImport(): void {
  Object.assign(IMP, {
    step: 1,
    file: null,
    fileBytes: null,
    sheets: null,
    sheetIndex: 0,
    headers: [],
    rows: [],
    mapping: {},
    fixes: {},
    skip: [],
    dupePolicy: 'skip',
    rowChoice: {},
    overflowChoice: {},
    filter: 'all',
    sendEmail: true,
    attemptKey: null,
    watch: null,
    batchId: null,
    issuing: false,
  });
}

/** Column suggestions run once per sheet and never map two columns onto the same field. */
function applySheet(sheets: Sheet[], index: number, t: DemoTemplate): void {
  const s = sheets[index];
  if (!s) return;
  IMP.sheetIndex = index;
  IMP.headers = s.headers;
  IMP.rows = s.rows;
  IMP.mapping = {};
  IMP.fixes = {};
  IMP.skip = [];
  const flds = importFields(t);
  const used = new Set<string>();
  for (const h of s.headers) {
    const f = suggestField(h, flds);
    IMP.mapping[h] = used.has(f) ? '' : f;
    if (f) used.add(f);
  }
}

const IconUpload = (): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M12 16V5" />
    <path d="M7 10l5-5 5 5" />
    <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
  </svg>
);

const IconCheck = (): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const IconEdit = (): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const IconX = (): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const batchStatusPill = (b: DemoBatch, db: DemoDatabase): ReactElement =>
  b.status === 'completed' ? (
    <span className="pill pill-active">✓ Tamamlandı</span>
  ) : b.status === 'completed_with_errors' ? (
    <span className="pill pill-danger">{`⚠ ${batchSummary(b, db).invalid} hatalı satır`}</span>
  ) : (
    <span className="pill pill-info">{b.status}</span>
  );

const pillDelivery = (d: string | null): ReactElement => {
  const [label, cls] = deliveryLabel(d);
  return <span className={`pill ${cls}`}>{label}</span>;
};

// ------------------------------------------------------------------------------- batch surfaces

const BATCHES_PER_PAGE = 25;

/**
 * The upload history, one page at a time.
 *
 * It used to be `db.batches` — fifty batches read at load, with every row of every one of them, and
 * no way at all to reach the fifty-first: not «on another page», unreachable, because nothing told
 * the screen there was more. The four numeric columns are now counts the SERVER took over each
 * batch's rows and credentials, so the table states totals instead of adding up a download.
 */
function BatchHistory({ db }: { db: DemoDatabase }): ReactElement {
  const navigate = useNavigate();
  const queries = useV07Queries();
  const [page, setPage] = useState(1);
  const read = useRead(`batches:${page}:${BATCHES_PER_PAGE}`, () =>
    queries.batchPage({ page, perPage: BATCHES_PER_PAGE }),
  );
  const list = read.value?.rows ?? [];
  const pages = read.value?.pages ?? 1;
  const shown = read.value?.page ?? page;
  const total = read.value?.total ?? 0;
  /* `?? null` on a property access is a new value only when the answer is; this is already stable. */
  useAdoptBatches(read.value?.rows ?? null);
  return (
    <div className="card">
      <div className="card-head">
        <h2>Geçmiş yüklemeler</h2>
        <Link className="btn btn-primary btn-sm" to="/admin/import">
          Yeni yükleme
        </Link>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Yükleme</th>
              <th>Şablon</th>
              <th className="num">Satır</th>
              <th className="num">Oluşturulan</th>
              <th className="num">Hatalı</th>
              <th className="num">Onay bekleyen</th>
              <th>Sonuç</th>
              <th>Tarih</th>
            </tr>
          </thead>
          <tbody>
            {read.status === 'loading' ? (
              <tr>
                <td colSpan={8} className="muted" aria-busy="true">
                  Yüklemeler okunuyor…
                </td>
              </tr>
            ) : read.status === 'error' && list.length === 0 ? (
              /* «Henüz yükleme yok» would report a server problem as an empty history. */
              <tr>
                <td colSpan={8}>
                  <div className="warn-box" role="alert">
                    <b>
                      {read.error?.kind === 'session'
                        ? 'Oturum sona erdi — yüklemeler okunamadı.'
                        : read.error?.kind === 'forbidden'
                          ? 'Toplu üretim kayıtlarını görme yetkiniz yok.'
                          : 'Yüklemeler okunamadı.'}
                    </b>{' '}
                    {read.error?.kind === 'session' ? null : (
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        id="impRetry"
                        onClick={read.retry}
                      >
                        Yeniden dene
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  Henüz yükleme yok.
                </td>
              </tr>
            ) : (
              list.map((b) => {
                const s = batchSummary(b, db);
                const t = db.templates.find((x) => x.id === b.templateId);
                const rel = t?.releases.find((r) => r.id === b.releaseId);
                return (
                  <tr
                    className="is-link"
                    data-batch={b.id}
                    key={b.id}
                    onClick={() => void navigate(`/admin/import?batch=${b.id}`)}
                  >
                    <td>
                      {/*
                       * The NAME first, and the file under it — the other way round was the defect:
                       * forty uploads called `kayit.xlsx` are forty identical rows. An upload nobody
                       * has named prints exactly what it printed before (`uploadLabel` falls back to
                       * the filename), so this row is unchanged until someone names it.
                       */}
                      <b style={{ fontWeight: 500 }} data-upload-label={b.id}>
                        {uploadLabel(b)}
                      </b>
                      <br />
                      <span className="small muted">
                        {[uploadSubLabel(b), b.createdBy].filter((x) => x !== null && x !== '').join(' · ')}
                      </span>
                    </td>
                    <td>
                      <span className="pill pill-neutral sq">{t ? t.code : b.templateId}</span>{' '}
                      <span className="small muted">{`v${rel?.releaseNumber ?? '?'}`}</span>
                    </td>
                    <td className="num tnum">{s.total}</td>
                    <td className="num tnum">{s.created}</td>
                    <td className="num tnum">{s.invalid}</td>
                    <td className="num tnum">{s.pending}</td>
                    <td>{batchStatusPill(b, db)}</td>
                    <td className="small muted nowrap">{fmtDateTime(b.createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
          {pages > 1 ? (
            <tfoot>
              <tr>
                <td colSpan={8}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span className="small muted">{`Sayfa ${shown} / ${pages} · ${fmtNum(total)} yükleme`}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        id="impPgPrev"
                        disabled={shown === 1}
                        onClick={() => setPage(shown - 1)}
                      >
                        ← Önceki
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        id="impPgNext"
                        disabled={shown === pages}
                        onClick={() => setPage(shown + 1)}
                      >
                        Sonraki →
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}

/** What the row's three right-hand columns need about the credential it produced. */
interface RowCredential {
  publicCode: string;
  viewState: string;
  deliveryStatus: string | null;
}

interface BatchRow {
  n: number;
  status: string;
  errors: string[];
  data: Record<string, string>;
  credentialId: string | null;
  /** The server's own facts about this row's credential; absent in the seed, which has the world. */
  credential: RowCredential | null;
}

const asRowCredential = (v: unknown): RowCredential | null => {
  if (v === null || typeof v !== 'object') return null;
  const c = v as Partial<RowCredential>;
  if (typeof c.publicCode !== 'string') return null;
  return {
    publicCode: c.publicCode,
    viewState: String(c.viewState ?? ''),
    deliveryStatus: typeof c.deliveryStatus === 'string' ? c.deliveryStatus : null,
  };
};

const asBatchRow = (r: unknown): BatchRow => {
  const x = r as Partial<BatchRow>;
  return {
    n: Number(x.n ?? 0),
    status: String(x.status ?? ''),
    errors: Array.isArray(x.errors) ? x.errors.map(String) : [],
    data: (x.data ?? {}) as Record<string, string>,
    credentialId: x.credentialId ?? null,
    credential: asRowCredential(x.credential ?? null),
  };
};

function BatchDetail({
  db,
  batch,
  fresh,
  onAgain,
  rowWindow,
  onRowPage,
  stale,
  onRetry,
}: {
  db: DemoDatabase;
  batch: DemoBatch;
  fresh: boolean;
  onAgain: (() => void) | null;
  /** Which slice of the rows is on screen, and how many there are in the batch. */
  rowWindow: { offset: number; perPage: number; total: number } | null;
  onRowPage: ((offset: number) => void) | null;
  /** True when the latest read of THIS batch failed and what is shown is the previous answer. */
  stale: boolean;
  onRetry: (() => void) | null;
}): ReactElement {
  const toast = useToast();
  const commands = useV07Commands();
  const queries = useV07Queries();
  const role = useV07Role();
  /*
   * THE FIRST E-MAIL OF THIS UPLOAD'S RECORDS, from the upload's own page.
   *
   * No new group screen and no second mechanism: the scope is this batch plus «no e-mail exists»,
   * resolved by the server, and the window is the same one the register and the detail screen open.
   * The control is drawn only when the server says there is something to send — a button that would
   * answer «0 kayıt» is not an offer.
   */
  const mayResend = role === null || role === 'owner' || role === 'admin' || role === 'operator';
  /*
   * NAMING THE UPLOAD (migration 028) — `import.create`, the cell that made it.
   *
   * The same three roles, because naming an upload is part of running one; a viewer reads the
   * history and does not rearrange it. The control is not drawn for a viewer rather than drawn and
   * refused, so nobody is offered a button the server would turn down.
   */
  const mayName = mayResend;
  /** The text being edited, or null when the dialog is closed. Empty string clears the name. */
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [firstSendOpen, setFirstSendOpen] = useState(false);
  const firstSendEligible = useRead(`first-send:batch:${batch.id}`, async () => {
    if (!mayResend) return 0;
    const r = await commands.previewFirstSend({
      scope: { filter: { batchId: batch.id, noEmail: true } },
    });
    return r.ok && r.preview ? r.preview.eligible : 0;
  });
  const t = db.templates.find((x) => x.id === batch.templateId);
  const ver = t?.releases.find((r) => r.id === batch.releaseId) ?? null;
  const s = batchSummary(batch, db);
  const hf = ver ? holderFieldKey(ver as unknown as Layout) : 'recipient_name';
  /*
   * Whether this run's records get a claim link at all.
   *
   * Only `require_recipient_acceptance` mints a claim token and queues the invitation
   * (`domain/issuance/plan.ts`: `needsClaimToken`), so on the other two policies «onay e-postası»
   * describes something that never happens. The acceptance run read exactly that sentence over an
   * `issue_immediately` template.
   */
  const needsClaim = ver?.publicationPolicy === 'require_recipient_acceptance';
  /* The PAGE is what the table draws; `batch.rows` is what the batch has, and they are not the same. */
  const rows = (batch.rowPage ?? batch.rows).map(asBatchRow);
  const rowPerPage = rowWindow?.perPage ?? rows.length;
  const rowTotal = rowWindow?.total ?? rows.length;
  const rowPages = rowPerPage > 0 ? Math.max(1, Math.ceil(rowTotal / rowPerPage)) : 1;
  const rowShown = rowPerPage > 0 ? Math.floor((rowWindow?.offset ?? 0) / rowPerPage) + 1 : 1;
  /*
   * The row's credential, as the ROW describes it.
   *
   * It used to be a lookup in `db.credentials`, which is the register's page: a batch opened from its
   * own link found nothing there and drew a dash in all three columns, and the same batch drew codes
   * and pills once the register had been visited. The read carries these facts now. The store lookup
   * stays as the SEED's path — there the store really is the whole world.
   */
  const credOf = (r: BatchRow): RowCredential | null => {
    if (r.credential) return r.credential;
    if (!r.credentialId) return null;
    const c = db.credentials.find((x) => x.id === r.credentialId);
    return c === undefined
      ? null
      : { publicCode: c.publicCode, viewState: credentialViewState(c), deliveryStatus: c.deliveryStatus };
  };

  const statePill = (state: string): ReactElement => {
    const st = state as ViewState;
    /* An unknown state is named rather than drawn as a classless pill with no text. */
    const cls = STATUS_PILL[st] ?? 'pill-neutral';
    const icon = STATUS_ICON[st] ?? '·';
    const label = STATUS_TR[st] ?? state;
    return <span className={`pill ${cls}`}>{`${icon} ${label}`}</span>;
  };

  const rowPill = (r: BatchRow): ReactElement => {
    /* The server says `needs_resolution` / `failed` where the reference says `invalid`. */
    const outcome = batchRowOutcome(r.status);
    if (outcome === 'invalid')
      return <span className="pill pill-danger">{`✕ ${r.errors[0] ?? 'Hatalı'}`}</span>;
    if (outcome === 'duplicate') return <span className="pill pill-neutral">≈ Mükerrer atlandı</span>;
    if (outcome === 'skipped') return <span className="pill pill-neutral">– Atlandı</span>;
    const c = credOf(r);
    if (!c) return <span className="pill pill-neutral">—</span>;
    return statePill(c.viewState);
  };

  /*
   * «Hatalı satırları indir» — all of them, not the rejected rows of the page on screen.
   *
   * With the rows paged, building the file from `rows` would have written a file named «hatalı
   * satırlar» containing some of them, which is the kind of partial answer this whole round is about.
   * The rejected rows are read on the click, and a read that fails says so instead of saving a file.
   */
  const downloadErrors = async (): Promise<void> => {
    let inv: BatchRow[];
    try {
      inv = (await queries.batchErrorRows(batch.id)).map(asBatchRow);
    } catch {
      toast('Hatalı satırlar okunamadı; dosya oluşturulmadı.');
      return;
    }
    downloadText(
      `${batch.filename.replace(/\.[^.]+$/, '')}-hatali.csv`,
      [
        'Satır;Sahip;E-posta;Hata',
        ...inv.map((r) => `${r.n};"${r.data[hf] ?? ''}";"${r.data.email ?? ''}";"${r.errors.join(' | ')}"`),
      ].join('\n'),
      'text/csv',
    );
  };

  const resend = async (): Promise<void> => {
    const r = await commands.requeueBouncedEmails({ batchId: batch.id });
    toast(r.ok ? `${r.message ?? '0'} e-posta yeniden kuyruğa alındı` : (r.message ?? 'Gönderilemedi'));
  };

  return (
    <div className="stack">
      {/*
       * A refresh of THIS batch that failed.
       *
       * The previous answer is still the best information there is, so it stays — but it is last-read,
       * not current, and saying so is the difference between a screen that is behind and a screen that
       * is lying. The screen never entered its error branch here, because a batch it had already loaded
       * was drawn from that earlier answer without a word.
       */}
      {stale ? (
        <div className="warn-box" id="bdStale" role="status" style={{ fontSize: 14 }}>
          <b>Bu yükleme yenilenemedi.</b>{' '}
          {'Aşağıdakiler en son okunan bilgiler; sayılar ve satırlar değişmiş olabilir.'}
          {onRetry === null ? null : (
            <>
              {' '}
              <button className="btn btn-secondary btn-sm" type="button" id="bdRetry" onClick={onRetry}>
                Yeniden dene
              </button>
            </>
          )}
        </div>
      ) : null}
      {fresh ? (
        <div className="ok-box" style={{ fontSize: 14 }}>
          <b>Yükleme tamamlandı.</b>
          {` ${s.created} kayıt oluşturuldu; ${mailSentence(batch.sendEmail === true, needsClaim, s.created)}.`}
        </div>
      ) : null}
      <div className="card">
        <div className="card-head">
          <div>
            <span className="eyebrow">Batch detayı</span>
            <h2 style={{ marginTop: 4 }} id="bdName">
              {uploadLabel(batch)}
            </h2>
            {/* Immediately after the name it changes, which is also where the decision anchors it. */}
            {mayName ? (
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="bdRename"
                onClick={() => setRenaming(uploadName(batch) ?? '')}
              >
                Adını değiştir
              </button>
            ) : null}
            {uploadSubLabel(batch) === null ? null : (
              <span className="small muted">{uploadSubLabel(batch)}</span>
            )}
          </div>
          {batchStatusPill(batch, db)}
        </div>
        <div className="card-pad">
          <dl className="dl">
            <dt>Şablon</dt>
            <dd>
              <Link to={`/admin/templates/${batch.templateId}/edit`}>{t ? t.name : batch.templateId}</Link>
              {/* The reference reads `ver.version`, which a release does not carry — it carries
                  `releaseNumber` — so upstream this line renders «vundefined», and so did this one.
                  Corrected on the Product Owner's own report from the acceptance run: the batch's
                  records really are pinned to a release, and the number of it is the fact this line
                  exists to state. The ledger's template column has always read `releaseNumber`, so
                  the two now agree instead of contradicting each other. */}
              {` · v${ver ? ver.releaseNumber : '?'} `}
              <span className="small muted">(bu batch'in kayıtlarına sabitlendi)</span>
            </dd>
            <dt>Başlatan</dt>
            <dd>{batch.createdBy}</dd>
            <dt>Tarih</dt>
            <dd>{fmtDateTime(batch.createdAt)}</dd>
            <dt>Dil / e-posta</dt>
            <dd>
              {`${(batch.lang || 'tr').toUpperCase()} · ${
                batch.sendEmail !== true
                  ? 'gönderim kapalı'
                  : needsClaim
                    ? 'onay e-postası'
                    : 'e-posta gönderilmez (bu politikada onay bağlantısı yok)'
              }`}
            </dd>
            <dt>Idempotency</dt>
            <dd className="small muted">{String(batch.attemptKey ?? batch.id)}</dd>
          </dl>
        </div>
      </div>
      <div className="summary-band" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="item">
          <b>{s.total}</b>
          <span>toplam satır</span>
        </div>
        <div className="item ok">
          <b>{s.created}</b>
          <span>oluşturulan</span>
        </div>
        <div className="item bad">
          <b>{s.invalid}</b>
          <span>hatalı</span>
        </div>
        <div className="item dupe">
          <b>{s.duplicates}</b>
          <span>mükerrer atlandı</span>
        </div>
        <div className="item">
          <b>{s.pending}</b>
          <span>onay bekleyen</span>
        </div>
        <div className="item ok">
          <b>{s.active}</b>
          <span>aktif (yayında)</span>
        </div>
        <div className="item">
          <b>{s.delivered}</b>
          <span>e-posta teslim edildi</span>
        </div>
        <div className="item bad">
          <b>{s.bounced}</b>
          <span>e-posta geri döndü</span>
        </div>
      </div>
      <div className="card">
        <div className="card-head">
          <h2>Satır bazlı sonuç</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {s.invalid ? (
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="bdErrCsv"
                onClick={() => void downloadErrors()}
              >
                Hatalı satırları indir
              </button>
            ) : null}
            {s.bounced ? (
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="bdResend"
                onClick={() => void resend()}
              >
                {`Başarısız e-postaları yeniden gönder (${s.bounced})`}
              </button>
            ) : null}
            {mayResend && (firstSendEligible.value ?? 0) > 0 ? (
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="bdFirstSend"
                onClick={() => setFirstSendOpen(true)}
              >
                {`İlk e-postayı gönder (${firstSendEligible.value ?? 0})`}
              </button>
            ) : null}
            <Link className="btn btn-primary btn-sm" to={`/admin/credentials?batch=${batch.id}`}>
              Oluşturulan credential'ları gör
            </Link>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="num">Satır</th>
                <th>Sahip</th>
                <th>E-posta</th>
                <th>Sonuç</th>
                <th>E-posta durumu</th>
                <th>Credential</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = credOf(r);
                const nm = r.data[hf] || r.data.recipient_name || r.data.organization_name || '—';
                return (
                  <tr
                    className={batchRowOutcome(r.status) === 'invalid' ? 'row-invalid' : ''}
                    data-row={r.n}
                    key={r.n}
                  >
                    <td className="num tnum muted">{r.n}</td>
                    <td>
                      <b style={{ fontWeight: 500 }}>{nm}</b>
                    </td>
                    <td className="small">{r.data.email ?? ''}</td>
                    <td>
                      {rowPill(r)}
                      {r.errors.length > 1 ? (
                        <div className="small muted">{r.errors.slice(1).join(' · ')}</div>
                      ) : null}
                    </td>
                    <td>{c ? pillDelivery(c.deliveryStatus) : '—'}</td>
                    <td>
                      {c ? (
                        <Link to={`/admin/credentials/${c.publicCode}`} className="mono small">
                          {shortCode(c.publicCode)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/*
             * The rows past the first hundred and twenty.
             *
             * They used to be a sentence — «… N satır daha» — and nothing else: true, and with no way
             * to reach them. Same hundred and twenty, as a page. The pager is drawn only when there is
             * more than one page, so a batch that fits on one keeps exactly the reference's table, and
             * the numbers above come from the batch's own counts rather than from the page.
             */}
            {rowPages > 1 ? (
              <tfoot>
                <tr>
                  <td colSpan={6}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span className="small muted" id="bdRowPage">
                        {`Sayfa ${rowShown} / ${rowPages} · ${fmtNum(rowTotal)} satır`}
                      </span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          type="button"
                          id="bdRowPrev"
                          disabled={rowShown === 1}
                          onClick={() => onRowPage?.((rowShown - 2) * rowPerPage)}
                        >
                          ← Önceki
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          type="button"
                          id="bdRowNext"
                          disabled={rowShown === rowPages}
                          onClick={() => onRowPage?.(rowShown * rowPerPage)}
                        >
                          Sonraki →
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {onAgain === null ? (
          <Link className="btn btn-ghost" to="/admin/import?tab=history">
            ← Geçmiş yüklemeler
          </Link>
        ) : (
          <button className="btn btn-ghost" type="button" id="bdAgain" onClick={onAgain}>
            Yeni yükleme
          </button>
        )}
      </div>
      {renaming === null ? null : (
        <Modal label="Yüklemeyi adlandır" onClose={() => setRenaming(null)}>
          <ModalHead title="Yüklemeyi adlandır" onClose={() => setRenaming(null)} />
          <div className="card-pad stack" style={{ gap: 10 }}>
            <label className="field" htmlFor="bdNameInput">
              Yükleme adı
              <input
                className="input"
                id="bdNameInput"
                value={renaming}
                maxLength={160}
                placeholder={uploadFilename(batch) ?? 'Örn. CXO 100 · Ekim'}
                onChange={(e) => setRenaming(e.target.value)}
              />
            </label>
            {/*
             * What a rename does NOT do, said where it is done. The operator who renames an upload
             * is told plainly that the records, their codes and their e-mails do not move, because
             * the obvious fear about renaming a thing that produced four hundred certificates is
             * that something downstream changes with it.
             */}
            <div className="help">
              Ad yalnız bu listede ve yükleme sayfasında görünür. Dosya adı, satırlar, üretilen sertifikalar,
              kodları ve gönderilmiş e-postalar değişmez. Boş bırakılırsa dosya adı gösterilir.
            </div>
          </div>
          <div className="card-foot">
            <button className="btn btn-ghost" type="button" onClick={() => setRenaming(null)}>
              Vazgeç
            </button>
            <button
              className="btn btn-primary"
              type="button"
              id="bdNameSave"
              disabled={renameBusy}
              onClick={() => {
                const next = renaming.trim();
                setRenameBusy(true);
                void commands
                  .renameIssuanceBatch({ batchId: batch.id, name: next === '' ? null : next })
                  .then((r) => {
                    if (!r.ok) {
                      toast(r.message ?? 'Ad kaydedilemedi');
                      return;
                    }
                    toast(`Yükleme adı kaydedildi: ${r.message ?? next}`);
                    setRenaming(null);
                    onRetry?.();
                  })
                  .finally(() => setRenameBusy(false));
              }}
            >
              {renameBusy ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </Modal>
      )}
      {firstSendOpen ? (
        <FirstSendPanel
          scope={{ filter: { batchId: batch.id, noEmail: true } }}
          what="bu yüklemenin kayıtları"
          onClose={() => {
            setFirstSendOpen(false);
            /* What is still eligible changed, so the control's own number is asked again. */
            firstSendEligible.retry();
          }}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------------------------- the steps

function TemplateStep({
  db,
  paint,
  onNext,
}: {
  db: DemoDatabase;
  paint: () => void;
  onNext: () => void;
}): ReactElement {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const list = db.templates
    .slice()
    .sort(
      (a, b) =>
        (a.status === 'published' ? 0 : 1) - (b.status === 'published' ? 0 : 1) ||
        a.name.localeCompare(b.name, 'tr'),
    );
  const q = trLower(query.trim());

  const downloadSample = (): void => {
    const tt = db.templates.find((x) => x.id === IMP.tplId);
    if (!tt) return;
    downloadText(`ornek_${slugify(tt.name)}.csv`, sampleCSV(tt), 'text/csv');
    toast('Örnek CSV hazırlandı');
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Hangi şablonla üretilecek?</h2>
        <span className="small muted">
          {`Yalnız yayınlanmış şablonlar seçilebilir · ${publishedTemplates(db).length} / ${db.templates.length}`}
        </span>
      </div>
      <div className="card-pad">
        <input
          className="input search"
          id="impTplSearch"
          placeholder="Şablon ara…"
          autoComplete="off"
          style={{ marginBottom: 10 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="stack" style={{ gap: 8, maxHeight: 420, overflow: 'auto' }} id="impTplList">
          {list.map((x) => {
            const rr = currentRelease(x);
            const pub = x.status === 'published' && !!rr;
            const lay = (rr ?? x.draft) as unknown as { validityPolicy?: { months?: number | null } };
            const months = lay.validityPolicy ? lay.validityPolicy.months : null;
            const name = trLower(`${x.name} ${x.code} ${templateTypeLabel(x)}`);
            const meta = `${draftTitle(x, 'tr')} · ${templateTypeLabel(x)} · ${
              (rr ?? x.draft).subjectType === 'organization' ? 'şirket' : 'kişi'
            } · ${months ? `${months} ay geçerli` : 'süresiz'} · gerekli alanlar: ${
              pub
                ? requiredFields(x)
                    .map((f) => importFieldLabel(x, f))
                    .join(', ')
                : '—'
            }${pub ? '' : ' · yayınlanmadığı için toplu üretimde kullanılamaz'}`;
            return (
              <label
                className={`radio ${pub ? '' : 'is-disabled'}`}
                data-name={name}
                key={x.id}
                hidden={!!q && !name.includes(q)}
              >
                <input
                  type="radio"
                  name="imptpl"
                  value={x.id}
                  checked={x.id === IMP.tplId}
                  disabled={!pub}
                  onChange={() => {
                    IMP.tplId = x.id;
                    paint();
                  }}
                />
                <span>
                  <b>
                    {`${x.name} `}
                    {pub ? (
                      <span className="small muted">{`v${rr?.releaseNumber}`}</span>
                    ) : (
                      <span className="pill pill-neutral none">{TEMPLATE_STATUS_TR[x.status]}</span>
                    )}
                  </b>
                  <span>{meta}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="card-foot" style={{ justifyContent: 'space-between' }}>
        <button className="btn btn-secondary" type="button" id="impSample" onClick={downloadSample}>
          Örnek Excel/CSV şablonunu indir
        </button>
        <button className="btn btn-primary" id="impNext1" type="button" onClick={onNext}>
          Devam
        </button>
      </div>
    </div>
  );
}

function UploadStep({
  template,
  paint,
  onBack,
  onNext,
}: {
  template: DemoTemplate;
  paint: () => void;
  onBack: () => void;
  onNext: () => void;
}): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [over, setOver] = useState(false);
  const rel = importRelease(template);

  const handle = async (f: File | undefined): Promise<void> => {
    if (!f) return;
    setError(null);
    setReading(true);
    try {
      const r = await parseSpreadsheet(f);
      IMP.file = f.name;
      IMP.fileBytes = await f.arrayBuffer();
      IMP.sheets = r.sheets;
      applySheet(r.sheets, 0, template);
      if (!IMP.rows.length) throw new Error('Seçili sayfada veri satırı bulunamadı.');
      setReading(false);
      paint();
    } catch (err) {
      setReading(false);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const useMock = (): void => {
    const m = mockRows(template);
    IMP.file = `${slugify(template.name)}_ornek.xlsx`;
    /* The sample list is generated in the tab and has no file behind it (§3: a demo control). */
    IMP.fileBytes = null;
    IMP.sheets = [{ name: 'Sayfa1', headers: m.headers, rows: m.rows }];
    applySheet(IMP.sheets, 0, template);
    paint();
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Alıcı listesini yükleyin</h2>
        <span className="pill pill-neutral sq">{`${template.code} · v${rel?.releaseNumber ?? '?'}`}</span>
      </div>
      <div className="card-pad stack">
        <label
          className={`dropzone${over ? ' is-over' : ''}`}
          id="dz"
          htmlFor="impFile"
          onDragEnter={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void handle(e.dataTransfer.files[0]);
          }}
        >
          <IconUpload />
          <b>CSV veya XLSX dosyasını buraya bırakın</b>
          <span className="small">
            {
              'ya da tıklayıp seçin · ilk satır başlık olmalı · kolon adları serbest, sonraki adımda eşlenir · eski ikili '
            }
            <code>.xls</code>
            {' desteklenmez'}
          </span>
        </label>
        <input
          type="file"
          id="impFile"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={(e) => void handle(e.target.files?.[0])}
        />
        {IMP.sheets && IMP.sheets.length > 1 ? (
          <div className="field">
            <label htmlFor="sheetSel">{`Çalışma sayfası (dosyada ${IMP.sheets.length} sayfa var)`}</label>
            <select
              className="select"
              id="sheetSel"
              value={IMP.sheetIndex}
              onChange={(e) => {
                if (IMP.sheets) applySheet(IMP.sheets, Number(e.target.value), template);
                paint();
              }}
            >
              {IMP.sheets.map((s, i) => (
                <option value={i} key={s.name}>
                  {`${s.name} · ${s.rows.length} satır`}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {IMP.file ? (
          <div className="file-chip">
            <IconCheck />
            <span>
              <b>{IMP.file}</b>
              {`${
                IMP.sheets && IMP.sheets.length > 1
                  ? ` · sayfa «${IMP.sheets[IMP.sheetIndex]?.name ?? ''}»`
                  : ''
              } · ${IMP.rows.length} satır · ${IMP.headers.length} kolon · okuyucu: yerleşik (çevrimdışı, ağ erişimi yok)`}
            </span>
          </div>
        ) : null}
        <div id="impErr">
          {reading ? (
            <div className="muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="spinner" />
              {' Dosya okunuyor…'}
            </div>
          ) : null}
          {error !== null ? <div className="warn-box">{error}</div> : null}
        </div>
        <div className="help">
          {'XLSX ve CSV tamamen çevrimdışı okunur: ZIP açma '}
          <code>DecompressionStream</code>
          {', XML ayrıştırma '}
          <code>DOMParser</code>
          {' ile yapılır — hiçbir CDN veya ağ isteği yoktur. Eski ikili '}
          <code>.xls</code>
          {' (BIFF) kapsam dışıdır ve reddedilir; dosya '}
          <code>.xlsx</code>
          {' veya '}
          <code>.csv</code>
          {' olarak kaydedilmelidir.'}
          {xlsxSupported() ? null : (
            <>
              {' '}
              <b>Bu tarayıcı DecompressionStream desteklemiyor; XLSX okunamaz, CSV kullanın.</b>
            </>
          )}
          <br />
          {`Güvenlik limitleri: en çok ${Math.round(XLSX_LIMITS.maxBytes / 1048576)} MB dosya, ${Math.round(
            XLSX_LIMITS.maxUnzippedBytes / 1048576,
          )} MB açılmış içerik, ${XLSX_LIMITS.maxSheets} sayfa, ${fmtNum(XLSX_LIMITS.maxRows)} satır, ${fmtNum(
            XLSX_LIMITS.maxCells,
          )} hücre, hücre başına ${fmtNum(
            XLSX_LIMITS.maxCellChars,
          )} karakter; makro ve dış bağlantı içeren dosyalar reddedilir, ZIP/XML bombasında erken durulur.`}
        </div>
        <div className="help" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {'Dosya yüklendiğinde hiçbir credential oluşmaz ve e-posta gitmez. '}
          <button className="btn btn-ghost btn-sm" id="impMock" type="button" onClick={useMock}>
            Örnek dosyayla devam et
          </button>
          <button
            className="btn btn-ghost btn-sm"
            id="impSample2"
            type="button"
            onClick={() =>
              downloadText(`ornek_${slugify(template.name)}.csv`, sampleCSV(template), 'text/csv')
            }
          >
            Örnek CSV indir
          </button>
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" id="impBack2" type="button" onClick={onBack}>
          Geri
        </button>
        <button
          className="btn btn-primary"
          id="impNext2"
          type="button"
          disabled={!IMP.rows.length}
          onClick={onNext}
        >
          Kolonları eşle
        </button>
      </div>
    </div>
  );
}

function MappingStep({
  template,
  paint,
  onBack,
  onNext,
}: {
  template: DemoTemplate;
  paint: () => void;
  onBack: () => void;
  onNext: () => void;
}): ReactElement {
  const flds = importFields(template);
  const required = requiredFields(template);
  const counts: Record<string, number> = {};
  for (const f of Object.values(IMP.mapping)) if (f) counts[f] = (counts[f] ?? 0) + 1;
  const dupMapped = Object.keys(counts).filter((f) => (counts[f] ?? 0) > 1);
  const mapped = new Set(Object.values(IMP.mapping).filter(Boolean));
  const missing = required.filter((f) => !mapped.has(f));
  const months = relValidityMonths(template);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Excel kolonlarını şablonun alanlarıyla eşleyin</h2>
        <span className="small muted">{`${IMP.file ?? ''} · ${template.name}`}</span>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Excel kolonu</th>
              <th>Örnek değer</th>
              <th>Şablon alanı</th>
            </tr>
          </thead>
          <tbody>
            {IMP.headers.map((h, i) => (
              <tr className={dupMapped.includes(IMP.mapping[h] ?? '') ? 'row-invalid' : ''} key={h}>
                <td>
                  <b style={{ fontWeight: 500 }}>{h}</b>
                </td>
                <td className="muted small">{String(IMP.rows[0]?.[i] ?? '').slice(0, 40)}</td>
                <td>
                  <select
                    className="select select-sm"
                    data-map={h}
                    aria-label={`${h} eşlemesi`}
                    value={IMP.mapping[h] ?? ''}
                    onChange={(e) => {
                      IMP.mapping[h] = e.target.value;
                      paint();
                    }}
                  >
                    <option value="">— eşleme yok —</option>
                    {flds.map((f) => {
                      const taken = mapped.has(f.key) && IMP.mapping[h] !== f.key;
                      return (
                        <option value={f.key} disabled={taken} key={f.key}>
                          {`${f.labelTr}${f.required ? ' *' : ''}${taken ? ' (başka kolona eşli)' : ''}`}
                        </option>
                      );
                    })}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-pad" id="mapNote">
        {dupMapped.length ? (
          <div className="warn-box">
            {'Aynı alan iki kolona eşlendi: '}
            <b>{dupMapped.map((f) => importFieldLabel(template, f)).join(', ')}</b>
            {'.'}
          </div>
        ) : missing.length ? (
          <div className="warn-box">
            {'Zorunlu alan eşlenmedi: '}
            <b>{missing.map((f) => importFieldLabel(template, f)).join(', ')}</b>
            {'.'}
          </div>
        ) : (
          <div className="ok-box">
            {`Zorunlu alanlar eşlendi (${required
              .map((f) => importFieldLabel(template, f))
              .join(', ')}). Veriliş tarihi boşsa bugün, geçerlilik boşsa şablonun ${
              months ? `${months} aylık` : 'süresiz'
            } varsayılanı, dil boşsa ${IMP.lang.toUpperCase()} kullanılır.`}
          </div>
        )}
        <p className="help" style={{ marginTop: 8 }}>
          Bu liste kodda sabit değildir: seçtiğiniz şablonun alan tanımlarından üretilir. Şablona yeni bir
          alan eklerseniz burada kendiliğinden görünür.
        </p>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" id="impBack3" type="button" onClick={onBack}>
          Geri
        </button>
        <button
          className="btn btn-primary"
          id="impNext3"
          type="button"
          disabled={missing.length > 0 || dupMapped.length > 0}
          onClick={onNext}
        >
          Doğrula
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------- step 4: validate

/**
 * A preview record that is never written anywhere — the e-mail card needs a credential-shaped
 * object to render, and real issuance must not go through here (it goes through the command).
 */
/**
 * What actually happens to the e-mail, said once.
 *
 * A run can be asked to send and still send nothing: the invitation exists only where a claim link
 * does. Saying «onay e-postası kuyruğa alındı» there would be a promise about a message the server
 * never queues — and no sender is invented to make it true.
 */
/** What becomes of a record on this policy, in the words the reference uses for each. */
function publishSentence(policy: string): string {
  if (policy === 'issue_immediately') return 'üretilir üretilmez yayınlanır';
  if (policy === 'admin_publish') return 'publicleşme yönetici yayınıyla';
  return 'publicleşme alıcı onayıyla';
}

function mailSentence(asked: boolean, needsClaim: boolean, created: number): string {
  if (!asked) return 'e-posta gönderilmedi';
  if (!needsClaim) return 'bu yayın politikasında onay bağlantısı yoktur, e-posta gönderilmedi';
  return `${created} onay e-postası kuyruğa alındı`;
}

function transientCredential(template: DemoTemplate, release: DemoRelease, r: ValidatedRow): DemoCredential {
  const lang = (r.lang === 'en' ? 'en' : 'tr') as 'tr' | 'en';
  const meta = release.localizedMetadata[lang] ?? release.localizedMetadata.tr ?? {};
  const publicCode = 'HPW-ONIZ-LEME0-0000-0000-0000';
  const uri = `${release.issuerProfileSnapshot.publicBase}/c/${publicCode}`;
  const renderData = buildRenderData(release, {
    lang,
    title: meta.title ?? '',
    issueDate: r.iss,
    expiresAt: r.exp,
    publicCode,
    credentialUri: uri,
    claims: r.values,
  });
  const at = prototypeNowUTC();
  return {
    id: 'preview',
    publicCode,
    canonicalPublicUrl: uri,
    credentialUri: uri,
    templateId: template.id,
    releaseId: release.id,
    releaseHash: release.releaseHash,
    release,
    subjectId: 'preview',
    subjectSnapshot: {
      subjectId: 'preview',
      subjectType: release.subjectType,
      canonicalDisplayName: r.values[holderFieldKey(release as unknown as Layout)] ?? '',
      snapshotHash: '',
    },
    claims: r.values,
    privateContactEmail: r.email,
    lang,
    issueDate: r.iss ?? '',
    expiresAt: r.exp,
    state: stateForPolicy(release.publicationPolicy) as DemoCredential['state'],
    publishedAt: null,
    revokedAt: null,
    supersedesCode: null,
    supersededByCode: null,
    renderDataSnapshot: renderData,
    renderManifest: {
      output: {
        width: canvasW(release.canvas),
        height: canvasH(release.canvas),
        format: 'png',
        scale: 1,
      },
    },
    renderManifestHash: '',
    revision: 1,
    revisions: [],
    claimToken: null,
    deliveryStatus: 'queued',
    batchId: null,
    sourceRow: r.n,
    createdBy: ACTOR,
    createdAt: at,
    updatedAt: at,
    events: [],
  };
}

/**
 * What this card says when the run sends no invitation.
 *
 * It describes the policy the template already carries and nothing else: no sender, no address, no
 * message, no link. The feature is not being added here — it is being reported as absent.
 */
function NoClaimEmailNotice({ policy }: { policy: string }): ReactElement {
  const what =
    policy === 'admin_publish'
      ? 'Kayıtlar üretildiğinde yönetici yayınlayana kadar bekler; alıcıya gönderilen bir onay bağlantısı yoktur.'
      : 'Kayıtlar üretilir üretilmez yayınlanır; alıcıya gönderilen bir onay bağlantısı yoktur.';
  return (
    <div className="info-box" id="impNoMail">
      <b>Bu yayın politikasında onay e-postası gönderilmez.</b>
      {` ${what} Public sayfanın bağlantısını alıcılarla siz paylaşırsınız.`}
    </div>
  );
}

function EmailPreview({
  template,
  release,
  first,
}: {
  template: DemoTemplate;
  release: DemoRelease;
  first: ValidatedRow | undefined;
}): ReactElement {
  const settings = useSettings();
  const E = settings.email as { senderName?: string; fromEmail?: string };
  /*
   * There is a message to preview only where there is a claim link to put in it.
   *
   * `require_recipient_acceptance` is the only policy that mints a claim token and queues the
   * invitation (`domain/issuance/plan.ts`, `needsClaimToken`). Under the other two the server sends
   * nothing at all — and this card was still drawing a message with a claim CTA, «Onaylamadan
   * hiçbir bilginiz yayınlanmaz» under it, and «Gönderim: Açık» beside it. Every one of those is a
   * promise about something that does not happen, so on those policies the card says what does.
   */
  if (release.publicationPolicy !== 'require_recipient_acceptance')
    return <NoClaimEmailNotice policy={release.publicationPolicy} />;
  if (!first)
    return <p className="muted">Oluşturulacak satır yok; önizleme için geçerli bir satır gerekir.</p>;
  const c = transientCredential(template, release, first);
  const lang = (c.lang === 'en' ? 'en' : 'tr') as 'tr' | 'en';
  const terms = buildTerms(release.credentialType, release.customTypeLabel ?? undefined, lang);
  const placeholders: Record<string, string> = {
    ...first.values,
    holder_name: String((c.subjectSnapshot as { canonicalDisplayName?: string }).canonicalDisplayName ?? ''),
    credential_title: String((release.localizedMetadata[lang] ?? release.localizedMetadata.tr)?.title ?? ''),
    credential_code: c.publicCode,
    credential_url: c.canonicalPublicUrl,
    claim_url: '',
    issue_date: String(
      c.renderDataSnapshot ? ((c.renderDataSnapshot as Record<string, string>).issue_date ?? '') : '',
    ),
    expiry_date: String(
      c.renderDataSnapshot ? ((c.renderDataSnapshot as Record<string, string>).expiry_date ?? '') : '',
    ),
    issuer_name: release.issuerProfileSnapshot.displayName,
  };
  const em = renderPreviewEmail(release, placeholders, terms.claimCta);
  const lines = em.body
    .split('\n')
    .filter((x, i, a) => !(x === '' && a[i - 1] === ''))
    .filter((x) => x !== '');

  return (
    <div className="split">
      <div className="stack" style={{ gap: 10 }}>
        <dl className="dl">
          <dt>Gönderen</dt>
          <dd>{`${E.senderName ?? ''} <${E.fromEmail ?? ''}>`}</dd>
          <dt>Alıcı</dt>
          <dd>
            {`${c.privateContactEmail ?? ''} `}
            <span className="small muted">{`(satır ${first.n})`}</span>
          </dd>
          <dt>Konu</dt>
          <dd>{em.subject}</dd>
          <dt>Dil</dt>
          <dd>{trUpper(c.lang)}</dd>
          <dt>Ana CTA</dt>
          <dd>
            {`${em.cta} → `}
            <span className="small muted">claim bağlantısı (public URL değil)</span>
          </dd>
          <dt>Gönderim</dt>
          <dd>
            {IMP.sendEmail ? (
              <span className="pill pill-active">Açık</span>
            ) : (
              <span className="pill pill-neutral">Kapalı — kayıtlar üretilir, e-posta gitmez</span>
            )}
          </dd>
        </dl>
      </div>
      <div className="email-frame">
        <div className="email-body" lang={c.lang}>
          <div className="brand-line">
            <i />
            {E.senderName ?? ''}
          </div>
          <div className="small muted" style={{ marginBottom: 14 }}>
            {`${c.lang === 'en' ? 'Subject' : 'Konu'}: ${em.subject}`}
          </div>
          <h1>{terms.ready}</h1>
          {lines.map((line) => (
            <p style={{ marginBottom: 10 }} key={line}>
              {line}
            </p>
          ))}
          {/* The image is the release's own render plan — sample values, never a real recipient's. */}
          <div
            className="visual"
            dangerouslySetInnerHTML={{ __html: credentialSVG(c, { lang, noWatermark: true }) }}
          />
          <a className="cta" href={em.ctaHash}>
            {em.cta}
          </a>
          <p className="small muted">
            {c.lang === 'en'
              ? 'Nothing is published until you accept.'
              : 'Onaylamadan hiçbir bilginiz yayınlanmaz.'}
          </p>
          <div className="foot">
            {`${release.issuerProfileSnapshot.displayName} · ${c.privateContactEmail ?? ''}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function RowFixModal({
  template,
  release,
  row,
  onClose,
  onSave,
}: {
  template: DemoTemplate;
  release: DemoRelease;
  row: ValidatedRow;
  onClose: () => void;
  onSave: (fix: Record<string, string>) => void;
}): ReactElement {
  const flds = importFields(template);
  const hf = holderFieldKey(release as unknown as Layout);
  const [fix, setFix] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    for (const f of flds) if (f.kind === 'value') base[f.key] = row.values[f.key] ?? '';
    base.email = row.email;
    base.issue_date = row.iss ?? '';
    base.expiry_date = row.exp ?? '';
    base.lang = row.lang;
    return base;
  });
  const set = (k: string, v: string): void => setFix((prev) => ({ ...prev, [k]: v }));

  const plan = resolveRenderPlan(release as unknown as Layout, { [hf]: fix[hf] ?? '' } as RenderData, {
    width: canvasW(release.canvas),
    height: canvasH(release.canvas),
  });
  const pr = plan.items.find((x) => x.kind === 'text' && x.layer.fieldKey === hf);

  const field = (key: string, label: string, type = 'text'): ReactElement => (
    <div className="field" key={key}>
      <label htmlFor={`fx_${key}`}>{label}</label>
      <input
        className="input"
        id={`fx_${key}`}
        data-fx={key}
        type={type}
        value={fix[key] ?? ''}
        onChange={(e) => set(key, e.target.value)}
      />
    </div>
  );

  return (
    <Modal label={`Satır ${row.n} düzelt`} onClose={onClose}>
      <ModalHead title={`Satır ${row.n} — düzelt`} onClose={onClose} />
      <div className="card-pad">
        {row.errors.length ? (
          <div className="warn-box">{row.errors.join(' · ')}</div>
        ) : row.warnings.length ? (
          <div className="info-box">{row.warnings.join(' · ')}</div>
        ) : null}
        {flds.filter((x) => x.kind === 'value').map((x) => field(x.key, x.labelTr))}
        {field('email', 'E-posta', 'email')}
        <div className="field-row">
          {field('issue_date', 'Veriliş tarihi', 'date')}
          {field('expiry_date', 'Geçerlilik', 'date')}
        </div>
        <div className="field">
          <label htmlFor="fx_lang">Dil</label>
          <select
            className="select"
            id="fx_lang"
            data-fx="lang"
            value={fix.lang ?? 'tr'}
            onChange={(e) => set('lang', e.target.value)}
          >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </select>
        </div>
        <div id="fxFit" className="help">
          {pr ? (
            pr.overflow ? (
              <span style={{ color: 'var(--warn)' }}>
                {`⚠ «${fix[hf] ?? ''}» minimum puntoda (${pr.size}px) bile sığmıyor.`}
              </span>
            ) : (
              `✓ Görselde ${pr.size}px ile sığıyor.`
            )
          ) : null}
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onClose}>
          Vazgeç
        </button>
        <button className="btn btn-primary" id="fxSave" type="button" onClick={() => onSave(fix)}>
          Kaydet ve yeniden doğrula
        </button>
      </div>
    </Modal>
  );
}

function ConfirmIssueModal({
  template,
  release,
  decisions,
  onClose,
  onGo,
}: {
  template: DemoTemplate;
  release: DemoRelease;
  decisions: { r: ValidatedRow; d: Decision }[];
  onClose: () => void;
  onGo: () => void;
}): ReactElement {
  const create = decisions.filter((x) => x.d === 'issue');
  const inv = decisions.filter((x) => x.d === 'invalid').length;
  const dup = decisions.filter((x) => x.d === 'skipped_duplicate').length;
  const sk = decisions.filter((x) => x.d === 'skipped').length;
  const fit = create.filter((x) => x.r.fitOverflow).length;
  const policy = release.publicationPolicy;
  /* Only this policy mints a claim token and queues the invitation (`needsClaimToken`), so it is
     the only one where «onay e-postası» is a thing that will happen. */
  const willMail = IMP.sendEmail && policy === 'require_recipient_acceptance';
  const emails = willMail ? create.length : 0;
  const noEmail = willMail ? 0 : create.length;
  const policyRec =
    policy === 'issue_immediately'
      ? {
          tr: 'Üretilir üretilmez yayınlanır',
          help: 'Kayıt issuance işleminin sonunda doğrudan active/public olur.',
        }
      : policy === 'admin_publish'
        ? {
            tr: 'Yöneticinin yayınlaması gerekir',
            help: 'Kayıt pending_admin_publish durumunda bekler; yönetici komutuyla yayınlanır.',
          }
        : {
            tr: 'Alıcı onayı gerekir',
            help: 'Kayıt pending_acceptance durumunda açılır; alıcı geçerli claim bağlantısıyla onaylayınca yayınlanır.',
          };

  return (
    <Modal label="Onay" onClose={onClose} sticky>
      <ModalHead
        title={`${create.length} credential oluşturulsun${
          willMail ? ` ve ${emails} onay e-postası gönderilsin` : ''
        } mi?`}
        onClose={onClose}
      />
      <div className="card-pad">
        <dl className="dl">
          <dt>Oluşturulacak kayıt</dt>
          <dd>
            <b>{create.length}</b>
          </dd>
          <dt>Atlanacak mükerrer</dt>
          <dd>{dup}</dd>
          <dt>Hatalı satır</dt>
          <dd>{inv}</dd>
          <dt>Elle atlanan</dt>
          <dd>{sk}</dd>
          <dt>E-posta gönderilecek</dt>
          <dd>{emails}</dd>
          <dt>Yalnız üretilecek (e-posta yok)</dt>
          <dd>{noEmail}</dd>
          {fit ? (
            <>
              <dt>İki satırlı render (admin onayı)</dt>
              <dd>{`${fit} kayıt`}</dd>
            </>
          ) : null}
          <dt>Release</dt>
          <dd>
            {`${template.name} · v${release.releaseNumber} · `}
            <span className="mono small">{`${String(release.releaseHash).slice(0, 12)}…`}</span>
            {' (her kayda snapshot olarak sabitlenir)'}
          </dd>
          <dt>Yayın politikası</dt>
          <dd>{`${policyRec.tr} — ${policyRec.help}`}</dd>
        </dl>
        <div className="info-box">
          {
            "Her satır için benzersiz public kod üretilir; alıcı onayı gereken politikada ayrıca gizli, tek kullanımlık ve süreli bir claim token'ı oluşturulur (kayıtta yalnız SHA-256 özeti saklanır). Aynı satır ikinci kez işlenirse yeni kayıt oluşmaz: idempotency anahtarı "
          }
          <code>batchId + sourceRowNumber + sourceRowHash + releaseId</code>
          {" olarak Workspace'te kalıcı saklanır."}
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onClose}>
          Vazgeç
        </button>
        <button className="btn btn-primary" id="goIssue" type="button" onClick={onGo}>
          {`${create.length} Credential Oluştur${willMail ? ` ve ${emails} Onay E-postasını Gönder` : ''}`}
        </button>
      </div>
    </Modal>
  );
}

function ValidationStep({
  db,
  template,
  release,
  paint,
  onBack,
  onIssued,
}: {
  db: DemoDatabase;
  template: DemoTemplate;
  release: DemoRelease;
  paint: () => void;
  onBack: () => void;
  onIssued: (batchId: string) => void;
}): ReactElement {
  const toast = useToast();
  const commands = useV07Commands();
  const [fixRow, setFixRow] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState<{ pct: number; text: string; closable?: string } | null>(null);
  /*
   * «Stop READING», never «stop the run» — AND IT BELONGS TO THE RUN, NOT TO A MOUNT.
   *
   * The first version tied it to this component's lifetime: a ref, flipped by an unmount effect.
   * That was wrong in a way worth recording, because it looked right and failed silently. This
   * app renders under `StrictMode` and the wizard re-keys its step subtree, so the effect's
   * CLEANUP fires while a run is in flight — the loop watching the server was told to stop by a
   * re-render. Measured: the progress window closed the instant the acceptance landed, the
   * sentence about closing the page was never drawn, and the run finished on the server perfectly
   * well with nobody reading it (batch accepted 14:35:16.813, finished 14:35:20.564, 6/6 issued).
   *
   * So the signal is created per RUN, held in the module-level wizard state that already survives
   * those remounts, and flipped by ONE thing: the operator closing the progress window. Nothing a
   * re-render does can stop the reading, and nothing the reading does can stop the run.
   */
  IMP.watch ??= { aborted: false };
  const stopWatching = IMP.watch;

  const hf = holderFieldKey(release as unknown as Layout);
  const v = validateRows(db, {
    template,
    headers: IMP.headers,
    rows: IMP.rows,
    mapping: IMP.mapping,
    fixes: IMP.fixes,
    skip: IMP.skip,
    lang: IMP.lang,
  });
  const extraCols = importFields(template)
    .filter((f) => f.kind === 'value' && f.key !== hf)
    .slice(0, 2);

  const invalid = v.filter((r) => r.errors.length);
  const dupes = v.filter((r) => !r.errors.length && r.dupe && !r.skipped);
  const skipped = v.filter((r) => r.skipped && !r.errors.length);
  const fitRows = v.filter((r) => r.fitOverflow && !r.errors.length && !r.skipped);
  const ctx: DecisionContext = {
    dupePolicy: IMP.dupePolicy,
    rowChoice: IMP.rowChoice,
    overflowChoice: IMP.overflowChoice,
  };
  const decisions = v.map((r) => ({ r, d: decisionFor(r, ctx) }));
  const willCreate = decisions.filter((x) => x.d === 'issue').length;
  const undecided = decisions.filter((x) => x.d === 'undecided').length;
  const undecidedFit = decisions.filter((x) => x.d === 'undecided_overflow').length;
  const skipDupes = decisions.filter((x) => x.d === 'skipped_duplicate').length;
  /* Same rule as the confirmation's: an invitation exists only where a claim link does. */
  const canMail = release.publicationPolicy === 'require_recipient_acceptance';
  const willMail = IMP.sendEmail && canMail;
  const emailCount = willMail ? willCreate : 0;

  const dupeAll = v.filter((r) => r.dupe && !r.errors.length);
  const warnAll = v.filter((r) => r.warnings.length && !r.errors.length);
  const skippedAll = v.filter((r) => r.skipped);
  const list =
    IMP.filter === 'fit'
      ? fitRows
      : IMP.filter === 'invalid'
        ? invalid
        : IMP.filter === 'dupe'
          ? dupeAll
          : IMP.filter === 'warn'
            ? warnAll
            : IMP.filter === 'skipped'
              ? skippedAll
              : v;

  const rowStatus = (r: ValidatedRow): ReactElement =>
    r.errors.length ? (
      <span className="pill pill-danger">{`✕ ${r.errors[0]}`}</span>
    ) : r.skipped ? (
      <span className="pill pill-neutral">– Atlandı</span>
    ) : r.dupe ? (
      <span className="pill pill-info">≈ Olası mükerrer</span>
    ) : r.fitOverflow ? (
      <span className="pill pill-info">⚠ Sığmıyor</span>
    ) : r.warnings.length ? (
      <span className="pill pill-info">⚠ Uyarı</span>
    ) : (
      <span className="pill pill-active">✓ Hazır</span>
    );

  const chip = (id: string, label: string, n: number): ReactElement => (
    <button
      className={`chip ${IMP.filter === id ? 'is-on' : ''}`}
      data-f={id}
      type="button"
      key={id}
      onClick={() => {
        IMP.filter = id;
        paint();
      }}
    >
      {`${label} (${n})`}
    </button>
  );

  const toggleSkip = (n: number): void => {
    IMP.skip = IMP.skip.includes(n) ? IMP.skip.filter((x) => x !== n) : [...IMP.skip, n];
    paint();
  };

  /**
   * The issuance loop. Every row goes through the same idempotency key the contract names —
   * `batchId + sourceRowNumber + sourceRowHash + releaseId` — so processing the same file twice
   * produces the same records rather than a second set.
   *
   * API GAP: `credential.issue_batch` — the backend owns code generation, the claim token, the
   * render manifest and the outbox row; the demo writes an equivalent record into its own store.
   */
  const runIssue = async (): Promise<void> => {
    if (IMP.issuing) return;
    IMP.issuing = true;
    setConfirming(false);
    const existing = db.batches.find((b) => b.attemptKey === IMP.attemptKey);
    if (existing) {
      IMP.issuing = false;
      toast('Bu batch zaten işlendi — ikinci kez üretilmedi (idempotent)');
      onIssued(existing.id);
      return;
    }
    setProgress({ pct: 0, text: 'Kodlar üretiliyor…' });
    const at = prototypeNowUTC();
    const batchId = uid('imp');
    const total = decisions.length || 1;
    let done = 0;
    let created = 0;

    const rows: Record<string, unknown>[] = [];
    const newCredentials: DemoCredential[] = [];
    const taken = new Set(db.credentials.map((c) => c.publicCode));

    for (const { r, d } of decisions) {
      done++;
      if (done % 5 === 0 || done === total)
        setProgress({
          pct: Math.round((done / total) * 96),
          text: `Satır ${r.n} işleniyor — ${created} kayıt oluşturuldu…`,
        });
      const rowHash = await sha256Canonical({ n: r.n, email: trLower(r.email).trim(), values: r.values });
      const idemKey = `${batchId}|${r.n}|${rowHash.slice(0, 32)}|${release.id}`;
      const row: Record<string, unknown> = {
        n: r.n,
        idempotencyKey: idemKey,
        sourceRowHash: rowHash,
        data: { ...r.values, email: r.email },
        status: d === 'issue' ? 'issued' : d,
        errors: r.errors,
        warnings: r.warnings,
        credentialId: null,
      };
      if (d === 'issue') {
        const code = generateUniquePublicCode({
          ...db,
          credentials: [...db.credentials, ...newCredentials],
        } as DemoDatabase);
        taken.add(code);
        const lang = (r.lang === 'en' ? 'en' : 'tr') as 'tr' | 'en';
        const meta = release.localizedMetadata[lang] ?? release.localizedMetadata.tr ?? {};
        const uri = `${release.issuerProfileSnapshot.publicBase}/c/${code}`;
        const renderData = buildRenderData(release, {
          lang,
          title: meta.title ?? '',
          issueDate: r.iss,
          expiresAt: r.exp,
          publicCode: code,
          credentialUri: uri,
          claims: r.values,
        });
        const state = stateForPolicy(release.publicationPolicy) as DemoCredential['state'];
        const credential: DemoCredential = {
          id: uid('cred'),
          publicCode: code,
          canonicalPublicUrl: uri,
          credentialUri: uri,
          templateId: template.id,
          releaseId: release.id,
          releaseHash: release.releaseHash,
          release,
          subjectId: uid('subj'),
          subjectSnapshot: {
            subjectId: uid('subj'),
            subjectType: release.subjectType,
            canonicalDisplayName: r.primary,
            externalReference: null,
            snapshotHash: rowHash.slice(0, 32),
          },
          claims: r.values,
          privateContactEmail: r.email,
          lang,
          issueDate: r.iss ?? '',
          expiresAt: r.exp,
          state,
          publishedAt: state === 'active' ? at : null,
          revokedAt: null,
          supersedesCode: null,
          supersededByCode: null,
          renderDataSnapshot: renderData,
          renderManifest: {
            output: {
              width: canvasW(release.canvas),
              height: canvasH(release.canvas),
              format: 'png',
              scale: 1,
            },
          },
          renderManifestHash: rowHash,
          revision: 1,
          revisions: [],
          /* Rule 7: only the digest is stored. The raw token lives in the e-mail the backend
             sends and nowhere else, which is why the demo cannot offer a working claim link. */
          claimToken: null,
          deliveryStatus: willMail ? 'queued' : 'disabled',
          batchId,
          sourceRow: r.n,
          createdBy: ACTOR,
          createdAt: at,
          updatedAt: at,
          events: [
            {
              id: uid('ev'),
              type: 'created',
              command: 'importBatch',
              at,
              actor: ACTOR,
              actorType: 'admin',
              meta: { releaseId: release.id, batchId, row: r.n },
            },
          ],
        };
        row.credentialId = credential.id;
        newCredentials.push(credential);
        created++;
      }
      rows.push(row);
    }

    const batch: DemoBatch = {
      id: batchId,
      attemptKey: IMP.attemptKey,
      filename: IMP.file ?? '',
      templateId: template.id,
      releaseId: release.id,
      releaseHash: release.releaseHash,
      status: rows.some((x) => x.status === 'invalid') ? 'completed_with_errors' : 'completed',
      createdAt: at,
      createdBy: ACTOR,
      lang: IMP.lang,
      sendEmail: willMail,
      rows,
      issuedAt: at,
      origin: 'runtime',
    };

    /*
     * Everything above is the reference's own preview of the run. The command is what makes it
     * real: the demo records exactly this preview, and the product sends the file, the mapping and
     * the options to the backend, which re-reads and re-validates the list server-side and mints
     * its own public codes. What the next screen shows is therefore what was actually issued.
     */
    /*
     * WHAT THE BAR SHOWS WHILE THE SERVER WORKS.
     *
     * Everything above this line is the reference's own preview, counted in the browser. The run
     * itself happens on the server in slices — one real render per row per sealed language — so the
     * bar is fed by the server's own count of finished rows. It used to stop at the preview's
     * percentage and then sit still for however long the request took, which on a hundred rows was
     * minutes and ended in «Sunucuya ulaşılamadı».
     */
    let lastSeen: { phase: string; done: number; total: number } | null = null;
    /*
     * What may be said, and WHEN.
     *
     * «Bu sayfayı kapatabilirsiniz» is a promise, and it is only true after the server has the job
     * durably — so it is set by `onAccepted` below and never by this function, which also runs
     * while the file is still being uploaded. It is also only true where a worker will carry the
     * run: a deployment without one accepts the job and leaves it waiting, which is a real state
     * and must not be dressed up as «carry on, we have it».
     */
    /* A fresh signal for THIS run, so a previous run's «stop reading» cannot carry over. */
    const watch = { aborted: false };
    IMP.watch = watch;
    let closable: string | undefined;
    const onProgress = (p: { phase: string; done: number; total: number }): void => {
      lastSeen = p;
      const pct = p.total > 0 ? Math.round((p.done / p.total) * 96) : 2;
      const text =
        p.phase === 'creating'
          ? 'Dosya sunucuya yükleniyor…'
          : p.phase === 'accepting'
            ? 'Sunucuya iletiliyor…'
            : p.phase === 'validating'
              ? `Sunucu doğruluyor — ${p.done}/${p.total} satır`
              : p.phase === 'reconciling'
                ? 'Durum okunamadı; yeniden deneniyor…'
                : `Sertifikalar üretiliyor — ${p.done}/${p.total} satır`;
      setProgress({ pct, text, ...(closable ? { closable } : {}) });
    };
    /*
     * THE SERVER'S OWN ID FOR THIS JOB.
     *
     * The demo's `batchId` above is a locally minted `imp_…`, so comparing a server answer against
     * it could never be equal — which silently disabled the branch below that sends an operator to
     * a run the server stopped, and left them on the wizard with a generic sentence instead. It is
     * learnt from the acceptance, which is the first moment the server HAS an id to give.
     */
    let serverBatchId: string | null = null;
    const onAccepted = (a: { batchId: string; processing: boolean }): void => {
      serverBatchId = a.batchId;
      closable = runAcceptedNotice(a.processing);
      const sentence = closable;
      setProgress((cur) => (cur === null ? cur : { ...cur, ...(sentence ? { closable: sentence } : {}) }));
    };

    const outcome = await commands.createBatch({
      onProgress,
      onAccepted,
      signal: watch,
      batch,
      credentials: newCredentials,
      release,
      file: { name: IMP.file ?? '', bytes: IMP.fileBytes },
      columnMapping: { ...IMP.mapping },
      options: {
        lang: IMP.lang,
        /*
         * What the run will actually do, not what the box remembers.
         *
         * The control is offered on every policy and its stored value survives a policy change, so
         * sending `IMP.sendEmail` recorded an intention this policy makes impossible — and the
         * batch's own `options` are what the result screen reads back afterwards.
         */
        sendEmail: willMail,
        duplicateDecision: (IMP.dupePolicy || null) as 'skip' | 'issue' | 'error' | null,
        /*
         * The same defaults the preview above showed, sent rather than assumed. The server re-reads
         * the file and dates every row itself; a file with no date column has nothing to date a row
         * with unless the run says what it decided.
         */
        defaultIssueDate: decisions.find((x) => x.d === 'issue')?.r.iss ?? null,
        defaultExpiresAt: decisions.find((x) => x.d === 'issue')?.r.exp ?? null,
      },
    });
    IMP.issuing = false;
    if (!outcome.ok) {
      setProgress(null);
      /*
       * A run the SERVER stopped is a different thing from a request that was refused: the batch
       * exists, part of it may be done, and the operator has somewhere to look. So it says so and
       * opens the batch instead of leaving a dead end.
       */
      const stoppedRun: string | null =
        outcome.outcome === 'refused' && outcome.message && outcome.message === serverBatchId
          ? outcome.message
          : null;
      if (stoppedRun !== null) {
        toast('Üretim sunucuda durdu. Yüklemenin kendi sayfasında nerede kaldığı yazıyor.');
        onIssued(stoppedRun);
        return;
      }
      toast(outcome.message ?? 'Toplu üretim tamamlanamadı');
      return;
    }
    /*
     * An interrupted run is not a finished one and must not read like one. The batch and the rows
     * it managed are on the server; the operator is told that and sent to the batch, where the
     * server's own counts are printed. «Tamamlanamadı» here is the sentence that makes somebody
     * upload the same list a second time.
     */
    const seen = lastSeen as { done: number; total: number } | null;
    if (outcome.code === 'batch_accepted_not_processing') {
      /*
       * Accepted, durable, and NOT being worked: this deployment's background production is off.
       * Saying «sürüyor» here would promise progress nothing is making.
       */
      setProgress(null);
      toast(
        'Yükleme sunucuya kaydedildi ama bu dağıtımda arka plan üretimi kapalı; bir yetkili açana kadar beklemede kalır.',
      );
      onIssued(outcome.message ?? batchId);
      return;
    }
    if (outcome.code === 'batch_running_on_server') {
      /*
       * THIS PAGE STOPPED WATCHING — the run did not stop. The operator is sent to the batch, where
       * the server's own counts are printed and keep moving. «Tamamlanamadı» here is the sentence
       * that makes somebody upload the same list a second time.
       */
      setProgress(null);
      toast(
        seen && seen.total > 0
          ? `Üretim sunucuda sürüyor — ${seen.done}/${seen.total} satır. Bu sayfayı kapatabilirsiniz; durumu yüklemenin kendi sayfasından izleyebilirsiniz.`
          : 'Üretim sunucuda sürüyor. Bu sayfayı kapatabilirsiniz; durumu yüklemenin kendi sayfasından izleyebilirsiniz.',
      );
      onIssued(outcome.message ?? batchId);
      return;
    }
    setProgress({ pct: 100, text: `Tamamlandı — ${seen?.total ?? created} kayıt` });
    window.setTimeout(() => {
      setProgress(null);
      onIssued(outcome.message ?? batchId);
    }, 260);
  };

  const row = fixRow === null ? undefined : v.find((x) => x.n === fixRow);

  return (
    <div className="stack">
      <div className="summary-band">
        <div className="item">
          <b>{v.length}</b>
          <span>satır okundu</span>
        </div>
        <div className="item ok">
          <b>{willCreate}</b>
          <span>oluşturulacak</span>
        </div>
        <div className="item bad">
          <b>{invalid.length}</b>
          <span>hatalı — düzeltilmeden üretilmez</span>
        </div>
        <div className="item dupe">
          <b>{dupes.length}</b>
          <span>olası mükerrer</span>
        </div>
      </div>

      {dupes.length ? (
        <div className="card">
          <div className="card-head">
            <h2>{`${dupes.length} olası mükerrer bulundu`}</h2>
            <span className="small muted">
              {`anahtar: ${
                release.subjectType === 'organization'
                  ? `şablon + ${importFieldLabel(template, hf)} + dönem`
                  : `şablon + ${importFieldLabel(template, hf)} + e-posta`
              }`}
            </span>
          </div>
          <div className="card-pad" style={{ display: 'grid', gap: 8 }}>
            {[
              {
                key: 'skip',
                title: "Mevcut aktif credential'ı atla",
                help: 'Aynı sahibe ait aktif ya da onay bekleyen kayıt varsa yeni kayıt açılmaz.',
              },
              {
                key: 'issue',
                title: 'Yeni credential çıkar',
                help: 'Yeni kod, yeni claim bağlantısı ve yeni sayfa üretilir.',
              },
              {
                key: 'review',
                title: 'Tek tek incele',
                help: 'Her mükerrer satır için karar verin.',
              },
            ].map((opt) => (
              <label className="radio" key={opt.key}>
                <input
                  type="radio"
                  name="dupe"
                  value={opt.key}
                  checked={IMP.dupePolicy === opt.key}
                  onChange={() => {
                    IMP.dupePolicy = opt.key;
                    paint();
                  }}
                />
                <span>
                  <b>{opt.title}</b>
                  <span>
                    {opt.help}
                    {opt.key === 'review' && IMP.dupePolicy === 'review' && undecided ? (
                      <>
                        {' '}
                        <b style={{ color: 'var(--warn)' }}>{`${undecided} satır karar bekliyor.`}</b>
                      </>
                    ) : null}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {fitRows.length ? (
        <div className={undecidedFit ? 'warn-box' : 'info-box'}>
          <b>{`${fitRows.length} satırda değer minimum puntoda bile alana sığmıyor.`}</b>
          {' Otomatik iki satıra geçilmez: her satır için siz karar verirsiniz. '}
          {undecidedFit ? (
            <b style={{ color: 'var(--warn)' }}>{`${undecidedFit} satır karar bekliyor.`}</b>
          ) : (
            'Tüm kararlar verildi.'
          )}
          <div className="chips" style={{ marginTop: 8 }}>
            <button
              className="chip"
              type="button"
              data-fitall="twoLines"
              onClick={() => {
                for (const r of fitRows) IMP.overflowChoice[r.n] = 'twoLines';
                paint();
              }}
            >
              Tümü için «İki satır kullan»
            </button>
            <button
              className="chip"
              type="button"
              data-fitall="skip"
              onClick={() => {
                for (const r of fitRows) IMP.overflowChoice[r.n] = 'skip';
                paint();
              }}
            >
              Tümünü atla
            </button>
            <button
              className="chip"
              type="button"
              data-f="fit"
              onClick={() => {
                IMP.filter = 'fit';
                paint();
              }}
            >
              Taşan satırları listele
            </button>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>Önizleme</h2>
          <div className="chips">
            {chip('all', 'Tümü', v.length)}
            {chip('invalid', 'Hatalı', invalid.length)}
            {chip('dupe', 'Mükerrer', dupeAll.length)}
            {chip('warn', 'Uyarı', warnAll.length)}
            {chip('skipped', 'Atlanan', skippedAll.length)}
            {chip('fit', 'Sığmayan', fitRows.length)}
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="num">Satır</th>
                <th>{importFieldLabel(template, hf)}</th>
                {extraCols.map((f) => (
                  <th key={f.key}>{f.labelTr}</th>
                ))}
                <th>E-posta</th>
                <th>Veriliş</th>
                <th>Geçerlilik</th>
                <th>Dil</th>
                <th>Durum</th>
                {IMP.dupePolicy === 'review' ? <th>Mükerrer kararı</th> : null}
                <th>Taşma kararı</th>
                <th scope="col">
                  <span className="sr-only">İşlemler</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.slice(0, 80).map((r) => (
                <tr
                  className={
                    r.errors.length
                      ? 'row-invalid'
                      : r.dupe && !r.skipped
                        ? 'row-dupe'
                        : r.skipped
                          ? 'row-skipped'
                          : ''
                  }
                  key={r.n}
                >
                  <td className="num tnum muted">{r.n}</td>
                  <td>
                    <b style={{ fontWeight: 500 }}>{r.primary || <span className="muted">—</span>}</b>
                  </td>
                  {extraCols.map((f) => (
                    <td className="small" key={f.key}>
                      {r.values[f.key] || '—'}
                    </td>
                  ))}
                  <td className="small">{r.email || <span className="muted">—</span>}</td>
                  <td className="small tnum">
                    {r.iss ? fmtDate(r.iss, 'tr', 'short') : <span className="muted">—</span>}
                  </td>
                  <td className="small tnum">
                    {r.exp ? fmtDate(r.exp, 'tr', 'short') : r.errors.length ? '—' : 'süresiz'}
                  </td>
                  <td className="small">{r.lang.toUpperCase()}</td>
                  <td>
                    {rowStatus(r)}
                    {r.warnings.length && !r.errors.length ? (
                      <div className="small muted" style={{ marginTop: 3 }}>
                        {r.warnings.join(' · ')}
                      </div>
                    ) : null}
                    {r.errors.length > 1 ? (
                      <div className="small muted" style={{ marginTop: 3 }}>
                        {r.errors.slice(1).join(' · ')}
                      </div>
                    ) : null}
                  </td>
                  {IMP.dupePolicy === 'review' ? (
                    <td>
                      {r.dupe && !r.errors.length && !r.skipped ? (
                        <select
                          className="select select-sm"
                          data-choice={r.n}
                          aria-label={`Satır ${r.n} kararı`}
                          value={IMP.rowChoice[r.n] ?? ''}
                          onChange={(e) => {
                            IMP.rowChoice[r.n] = e.target.value;
                            paint();
                          }}
                        >
                          <option value="">Seçin…</option>
                          <option value="skip">Atla</option>
                          <option value="issue">Yeni çıkar</option>
                        </select>
                      ) : null}
                    </td>
                  ) : null}
                  <td>
                    {r.fitOverflow && !r.errors.length && !r.skipped ? (
                      <select
                        className="select select-sm"
                        data-fitchoice={r.n}
                        aria-label={`Satır ${r.n} taşma kararı`}
                        value={IMP.overflowChoice[r.n] ?? ''}
                        onChange={(e) => {
                          IMP.overflowChoice[r.n] = e.target.value;
                          paint();
                        }}
                      >
                        <option value="">Karar verin…</option>
                        <option value="twoLines">İki satır kullan</option>
                        <option value="skip">Bu satırı atla</option>
                      </select>
                    ) : null}
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="icon-btn"
                        type="button"
                        data-fix={r.n}
                        title="Satırı düzelt"
                        aria-label={`Satır ${r.n}: düzelt`}
                        onClick={() => setFixRow(r.n)}
                      >
                        <IconEdit />
                      </button>
                      <button
                        className="icon-btn"
                        type="button"
                        data-skip={r.n}
                        title={r.skipped ? 'Satırı geri al' : 'Bu satırı atla'}
                        aria-label={`Satır ${r.n}: ${r.skipped ? 'geri al' : 'atla'}`}
                        onClick={() => toggleSkip(r.n)}
                      >
                        {r.skipped ? '↺' : <IconX />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {list.length > 80 ? (
                <tr>
                  <td colSpan={12} className="muted small">
                    {`… ${list.length - 80} satır daha (prototipte ilk 80 gösteriliyor)`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" id="emailPreviewCard">
        <div className="card-head">
          <h2>Gönderim önizlemesi</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="check">
              <input
                type="checkbox"
                id="impSend"
                checked={IMP.sendEmail && canMail}
                disabled={!canMail}
                onChange={(e) => {
                  IMP.sendEmail = e.target.checked;
                  paint();
                }}
              />
              {canMail ? ' Onay e-postası gönder' : ' Onay e-postası gönder (bu politikada gönderilmez)'}
            </label>
            <select
              className="select select-sm"
              id="impLang"
              aria-label="Gönderim dili"
              value={IMP.lang}
              onChange={(e) => {
                IMP.lang = e.target.value === 'en' ? 'en' : 'tr';
                paint();
              }}
            >
              <option value="tr">Varsayılan dil: Türkçe</option>
              <option value="en">Varsayılan dil: English</option>
            </select>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="impTestMail"
              disabled={!canMail}
              onClick={() => {
                /* API GAP: delivery.send_test — a real test message needs the outbox and Resend. */
                if (!decisions.some((x) => x.d === 'issue')) {
                  toast('Test için geçerli satır yok');
                  return;
                }
                toast('Test e-postası gönderildi (simülasyon)');
              }}
            >
              Test e-postası gönder
            </button>
          </div>
        </div>
        <div className="card-pad" id="impEmailBody">
          <EmailPreview
            template={template}
            release={release}
            first={decisions.find((x) => x.d === 'issue')?.r}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-foot" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" id="impBack4" type="button" onClick={onBack}>
              Geri
            </button>
            {invalid.length ? (
              <button
                className="btn btn-secondary"
                id="impErrCsv"
                type="button"
                onClick={() =>
                  downloadText(
                    'hatali-satirlar.csv',
                    [
                      'Satır;Değer;E-posta;Hata',
                      ...invalid.map((r) => `${r.n};"${r.primary}";"${r.email}";"${r.errors.join(' | ')}"`),
                    ].join('\n'),
                    'text/csv',
                  )
                }
              >
                Hatalı satırları CSV indir
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="small muted">
              {`${invalid.length ? `${invalid.length} hatalı · ` : ''}${
                skipDupes ? `${skipDupes} mükerrer atlanacak · ` : ''
              }${skipped.length ? `${skipped.length} elle atlandı · ` : ''}${publishSentence(release.publicationPolicy)}`}
            </span>
            <button
              className="btn btn-primary btn-lg"
              id="impIssue"
              type="button"
              disabled={!(willCreate && !undecided && !undecidedFit)}
              onClick={() => setConfirming(true)}
            >
              {`${willCreate} Credential Oluştur${
                willMail ? ` ve ${emailCount} Onay E-postasını Gönder` : ''
              }`}
            </button>
          </div>
        </div>
      </div>

      {row ? (
        <RowFixModal
          template={template}
          release={release}
          row={row}
          onClose={() => setFixRow(null)}
          onSave={(fix) => {
            IMP.fixes[row.n] = fix;
            IMP.skip = IMP.skip.filter((x) => x !== row.n);
            setFixRow(null);
            toast(`Satır ${row.n} güncellendi ve yeniden doğrulandı`);
            paint();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmIssueModal
          template={template}
          release={release}
          decisions={decisions}
          onClose={() => setConfirming(false)}
          onGo={() => void runIssue()}
        />
      ) : null}

      {progress !== null ? (
        /*
         * CLOSABLE ONCE THE SERVER HAS THE JOB — and locked shut until then.
         *
         * While the file is still being uploaded there is nothing to leave behind, so the window
         * stays `sticky` exactly as it did. From the acceptance onwards the run is the server's and
         * this window is only a view of it: closing it stops the READING and sends the operator to
         * the batch, where the same counts keep moving. A window that could not be closed was the
         * screen quietly insisting the browser had to stay.
         */
        <Modal
          label="İşleniyor"
          onClose={
            progress.closable === undefined
              ? () => undefined
              : () => {
                  stopWatching.aborted = true;
                  setProgress(null);
                }
          }
          {...(progress.closable === undefined ? { sticky: true } : {})}
        >
          <div className="card-head">
            <h2>Credential'lar oluşturuluyor</h2>
          </div>
          <div className="card-pad">
            <div className="progress">
              <i id="pbar" style={{ width: `${progress.pct}%` }} />
            </div>
            <div id="pstep" className="muted small">
              {progress.text}
            </div>
            {progress.closable === undefined ? null : (
              <div id="pclosable" className="muted small">
                {progress.closable}
              </div>
            )}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------------------------ the screen

export function ImportScreen(): ReactElement {
  const db = useDemoData();
  const queries = useV07Queries();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [, setTick] = useState(0);
  const paint = (): void => setTick((n) => n + 1);

  const pubs = publishedTemplates(db);
  const chosen = db.templates.find((t) => t.id === IMP.tplId);
  if (chosen?.status !== 'published') IMP.tplId = pubs[0]?.id ?? null;
  const template = db.templates.find((t) => t.id === IMP.tplId);
  const release = importRelease(template);

  const batchParam = params.get('batch');
  const tab: 'new' | 'history' = params.get('tab') === 'history' || batchParam ? 'history' : 'new';
  const steps = ['Şablon', 'Excel/CSV', 'Kolon eşleme', 'Doğrula ve önizle', 'Sonuç'];
  /*
   * The batch that is OPEN is read here — with its rows, because this is the screen that shows them.
   * The history above does not: it prints counts the server took, which is the whole point of not
   * downloading twenty-five thousand row payloads to render two hundred integers.
   */
  const openId = batchParam ?? (IMP.step === 5 ? IMP.batchId : null);
  /*
   * Which page of the open batch's rows to read — in the ADDRESS, like every other list state here.
   *
   * The register's filter, its page and the open batch itself all live in the query string, and the row
   * page belongs with them: the address then says what is on screen, a reload shows the same page, and
   * leaving the batch and coming back to it starts at the top instead of silently resuming a page the
   * address never mentioned. It also means no state to reset when the batch changes.
   */
  const rowOffsetParam = Number(params.get('rows') ?? '0');
  const rowOffset = Number.isFinite(rowOffsetParam) && rowOffsetParam > 0 ? Math.floor(rowOffsetParam) : 0;
  /*
   * The offset is part of the QUESTION: «page two of this batch» is not the same question as «page
   * one», so the answer to one is never shown under the other's number. A failed refresh of the page
   * that IS on screen still keeps it, marked stale, because that is the same question.
   */
  const openRead = useRead(`batch:${openId ?? ''}#${rowOffset}`, () =>
    openId === null
      ? ({ kind: 'not_found' } as BatchLookup)
      : queries.batchById(openId, { limit: ROWS_PER_PAGE, offset: rowOffset }),
  );
  const rowWindowOf = (b: DemoBatch): { offset: number; perPage: number; total: number } => ({
    offset: typeof b.rowsOffset === 'number' ? b.rowsOffset : rowOffset,
    perPage: ROWS_PER_PAGE,
    total: typeof b.rowsTotal === 'number' ? b.rowsTotal : b.rows.length,
  });

  const goToRowPage = (offset: number): void => {
    const next = new URLSearchParams(params);
    if (offset > 0) next.set('rows', String(Math.floor(offset)));
    else next.delete('rows');
    /* Replaced, not pushed: paging a table is not a place in the history a Back button should revisit. */
    void navigate(`/admin/import?${next.toString()}`, { replace: true });
  };
  /* A read that FAILED while this batch was on screen — not a verdict, which replaces the screen. */
  const openStale = openRead.status === 'error' && openRead.value?.kind === 'found';
  const openBatch = openRead.value?.kind === 'found' ? openRead.value.batch : null;
  /* A stable array: `[openBatch]` inline is a new array per render, and adoption writes per array. */
  const openRows = useMemo(() => (openBatch === null ? null : [openBatch]), [openBatch]);
  useAdoptBatches(openRows);
  /*
   * The store's record, wearing THIS read's window.
   *
   * The store holds the batch and the read holds the page, and the screen needs both: the store's copy
   * is the one a command has just updated, and the page is the one that was asked for. Keeping the
   * window out of the store is what stops a page from being mistaken for the batch — the demo's reads
   * answer from the store, so a page written back there narrowed the seed every time it was read.
   */
  const withPage = (stored: DemoBatch, answer: DemoBatch | null): DemoBatch =>
    answer === null
      ? stored
      : {
          ...stored,
          ...(answer.rowPage === undefined ? {} : { rowPage: answer.rowPage }),
          ...(answer.rowsTotal === undefined ? {} : { rowsTotal: answer.rowsTotal }),
          ...(answer.rowsOffset === undefined ? {} : { rowsOffset: answer.rowsOffset }),
        };
  /*
   * The batch the wizard just ran. The store's copy is preferred only for the SAME batch — it is the
   * one carrying rows after a refresh — never as a stand-in for a batch this read has not confirmed.
   */
  const freshBatch =
    IMP.step === 5 && IMP.batchId && openBatch?.id === IMP.batchId
      ? withPage(db.batches.find((b) => b.id === IMP.batchId) ?? openBatch, openBatch)
      : undefined;
  const wizard = tab === 'new' && template && release;
  /* The stepper is empty on the ledger and on a batch page, as upstream leaves `#stepper` empty. */
  const showStepper = wizard && !(IMP.step === 5 && !freshBatch);

  let body: ReactElement;
  if (batchParam) {
    /*
     * The verdict decides, the store only enriches.
     *
     * Looking in the store first meant the previously opened batch stayed on screen while another one
     * loaded, and a `forbidden` answer about THIS one was hidden behind whatever the store still held.
     */
    const b =
      openBatch === null
        ? null
        : withPage(db.batches.find((x) => x.id === openBatch.id) ?? openBatch, openBatch);
    body = b ? (
      <BatchDetail
        db={db}
        batch={b}
        fresh={false}
        onAgain={null}
        rowWindow={rowWindowOf(b)}
        onRowPage={goToRowPage}
        stale={openStale}
        onRetry={openRead.retry}
      />
    ) : openRead.status === 'loading' ? (
      <div className="card card-pad muted" aria-busy="true">
        Yükleme okunuyor…
      </div>
    ) : (
      /* Four outcomes, not one: absent, refused, unreadable, or simply not answered yet. */
      <div className="card card-pad">
        <div className="warn-box" role="alert">
          <b>
            {openRead.status === 'error' && openRead.error?.kind === 'session'
              ? 'Oturum sona erdi — yükleme okunamadı.'
              : openRead.value?.kind === 'forbidden' || openRead.error?.kind === 'forbidden'
                ? 'Bu yüklemeyi görme yetkiniz yok.'
                : openRead.status === 'error'
                  ? 'Yükleme okunamadı.'
                  : 'Batch bulunamadı.'}
          </b>
          {/* Only a failed read is worth asking again; a verdict would answer the same way. */}
          {openRead.status !== 'error' || openRead.error?.kind === 'session' ? null : (
            <>
              {' '}
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="impOpenRetry"
                onClick={openRead.retry}
              >
                Yeniden dene
              </button>
            </>
          )}
        </div>
      </div>
    );
  } else if (tab === 'history') {
    body = <BatchHistory db={db} />;
  } else if (!template || !release) {
    body = (
      <div className="card card-pad">
        <p className="muted">Toplu üretim için önce en az bir şablon yayınlayın.</p>
        <Link className="btn btn-primary btn-sm" to="/admin/templates/new" style={{ justifySelf: 'start' }}>
          + Yeni Şablon
        </Link>
      </div>
    );
  } else if (IMP.step === 5 && freshBatch) {
    body = (
      <BatchDetail
        db={db}
        batch={freshBatch}
        fresh
        onAgain={() => {
          resetImport();
          paint();
        }}
        rowWindow={rowWindowOf(freshBatch)}
        onRowPage={goToRowPage}
        /* The run's own result view is read the same way, so it says the same thing when a read fails. */
        stale={openStale}
        onRetry={openRead.retry}
      />
    );
  } else if (IMP.step === 1) {
    body = (
      <TemplateStep
        db={db}
        paint={paint}
        onNext={() => {
          IMP.step = 2;
          paint();
        }}
      />
    );
  } else if (IMP.step === 2) {
    body = (
      <UploadStep
        template={template}
        paint={paint}
        onBack={() => {
          IMP.step = 1;
          paint();
        }}
        onNext={() => {
          IMP.step = 3;
          paint();
        }}
      />
    );
  } else if (IMP.step === 3) {
    body = (
      <MappingStep
        template={template}
        paint={paint}
        onBack={() => {
          IMP.step = 2;
          paint();
        }}
        onNext={() => {
          if (!IMP.attemptKey) IMP.attemptKey = uid('attempt');
          IMP.step = 4;
          IMP.filter = 'all';
          paint();
        }}
      />
    );
  } else {
    body = (
      <ValidationStep
        db={db}
        template={template}
        release={release}
        paint={paint}
        onBack={() => {
          IMP.step = 3;
          paint();
        }}
        onIssued={(batchId) => {
          IMP.batchId = batchId;
          IMP.step = 5;
          paint();
        }}
      />
    );
  }

  return (
    <section data-screen="import">
      <div className="page-head">
        <div>
          <span className="eyebrow">Excel / CSV ile toplu üretim</span>
          <h1>Toplu Üretim</h1>
          <p className="lead">
            Dosya yüklenir yüklenmez kimseye e-posta gitmez: önce kolon eşleme, sonra doğrulama ve önizleme;
            kayıtlar yalnız onayınızla oluşturulur ve alıcı onaylayana kadar yayınlanmaz.
          </p>
        </div>
      </div>
      <div className="tabs" id="importTabs">
        <Link to="/admin/import" className={tab === 'new' ? 'is-active' : ''}>
          Yeni Toplu Üretim
        </Link>
        <Link to="/admin/import?tab=history" className={tab === 'history' ? 'is-active' : ''}>
          {`Geçmiş Yüklemeler (${db.batches.length})`}
        </Link>
      </div>
      <div className="stepper" id="stepper">
        {showStepper
          ? steps.map((s, i) => (
              <div
                className={`step ${IMP.step === i + 1 ? 'is-current' : IMP.step > i + 1 ? 'is-done' : ''}`}
                key={s}
              >
                <span className="n">{IMP.step > i + 1 ? '✓' : i + 1}</span>
                <span>{s}</span>
              </div>
            ))
          : null}
      </div>
      <div id="importBody">{body}</div>
    </section>
  );
}
